import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { ProductService, CompanyServices } from "$services";
import { addProduct } from "$api/productApi";
import useReferencing from "$hooks/useReferencing";
import useReferencingCategory from "$hooks/useReferencingCategory";
import useAuth from "$hooks/useAuth";
import { FieldValues } from "$hooks/useReferencingForm";
import { ProductReference } from "$types/ProductReference";
import { Reception } from "$types";
import { getProductQuantityData } from "$utils/orders";
import { queryClient } from "$utils/queryClient";
import { CacheInvalidationAction } from "$utils/cache";

enum DeleteStatus {
  NOT_STARTED = "notStarted",
  PENDING = "pending",
  DELETED = "deleted",
}

interface UseProductReferenceProps {
  companyId: string;
  hasReferencingCategory: boolean;
  isReadOnly: boolean;
  listIndex: number;
  nestIndex: number;
  productReference: ProductReference;
  reception: Reception;
  openDeleteModal: boolean;
  confirmedDeletions: boolean;
  setDeletedItems: React.Dispatch<React.SetStateAction<number[]>>;
}

export const useProductReference = ({
  companyId,
  hasReferencingCategory,
  isReadOnly,
  listIndex,
  nestIndex,
  productReference,
  reception,
  openDeleteModal,
  confirmedDeletions,
  setDeletedItems,
}: UseProductReferenceProps) => {
  const { token } = useAuth();
  const { referencingCategory } = useReferencingCategory(companyId);
  const { debouncedIsReferenceUnique } = useReferencing(companyId);

  const { productQuantity } = reception;
  const { productReference: initialReference } = productReference;

  const [stateReferenceUnique, setStateReferenceUnique] = useState(true);
  const [deleteStatus, setDeleteStatus] = useState<DeleteStatus>(
    DeleteStatus.NOT_STARTED
  );
  const [currentSearch, setCurrentSearch] = useState("");
  const [productFilter, setProductFilter] = useState([]);
  const [productFilterInitial, setProductFilterInitial] = useState([]);
  const [openProductForm, toggleOpenProductForm] = useState<boolean>(false);
  const [productError, setProductError] = useState<any>(null);
  const [companyObject, setCompanyObject] = useState(null);
  const [prefilledReference, setPrefilledReference] = useState<string>("");

  const fieldPath =
    `productReferenceLists.${listIndex}.productReferences.${nestIndex}` as const;
  const {
    control,
    formState: { errors, touchedFields },
    getValues,
    setValue,
    clearErrors,
  } = useFormContext<FieldValues>();

  const isTouched =
    touchedFields?.productReferenceLists &&
    touchedFields?.productReferenceLists[listIndex]?.productReferences[
      nestIndex
    ]?.productReference;

  const newProductReference = useWatch({
    control,
    name: `${fieldPath}.productReference`,
  });

  const { foundPQ } = getProductQuantityData(
    productReference,
    productQuantity,
    nestIndex
  );
  const { receivedQuantity } = foundPQ || {};

  const hasAtLeastOneReceived = (receivedQuantity: number) =>
    !Number.isNaN(receivedQuantity) && receivedQuantity > 0;

  const fetchCompanyObject = useCallback(async () => {
    if (reception.company && typeof reception.company === "string") {
      try {
        let company = await queryClient.getQueryData([
          CacheInvalidationAction.GET_COMPANY_BY_ID,
          reception.company,
        ]);

        if (!company) {
          company = await queryClient.fetchQuery({
            queryKey: [
              CacheInvalidationAction.GET_COMPANY_BY_ID,
              reception.company,
            ],
            queryFn: () => CompanyServices.getCompanyById(reception.company),
            gcTime: 60 * 1000,
          });
          await queryClient.setQueryData(
            [CacheInvalidationAction.GET_COMPANY_BY_ID, reception.company],
            company
          );
        }

        setCompanyObject(company);
      } catch (error) {
        console.error("Error fetching company:", error);
      }
    } else if (typeof reception.company === "object") {
      setCompanyObject(reception.company);
    }
  }, [reception.company]);

  useEffect(() => {
    fetchCompanyObject();
  }, [fetchCompanyObject]);

  const getProducts = useCallback(async () => {
    if (currentSearch?.length >= 3) {
      const products = await ProductService.getProductByReference({
        ...(currentSearch && { completeReference: currentSearch }),
        company: companyId,
        referencingCategory: referencingCategory?.id,
        isActive: true,
      });
      setProductFilter(products);
    }
  }, [companyId, currentSearch, referencingCategory?.id]);

  useEffect(() => {
    getProducts();
  }, [getProducts]);

  const fetchInitialValues = useCallback(async () => {
    if (newProductReference) {
      try {
        const products = await ProductService.getProductsByReferencesAndCompany(
          [newProductReference],
          companyId,
          true
        );
        setProductFilterInitial(products || []);
      } catch (error) {
        console.error("Error fetching initial product:", error);
        setProductFilterInitial([]);
      }
    }
  }, [companyId, newProductReference]);

  useEffect(() => {
    if (companyId && newProductReference) {
      fetchInitialValues();
    }
  }, [fetchInitialValues, companyId, newProductReference]);

  const getValueProduct = useCallback(
    (value) => {
      if (!value) return null;

      let product = productFilterInitial.find((p) => p.reference === value);
      if (product) return product;

      product = productFilter.find((p) => p.reference === value);
      if (product) return product;

      return {
        reference: value,
        designation: "",
        secondReference: "",
      };
    },
    [productFilterInitial, productFilter]
  );

  const onProductSubmit = async (data: any) => {
    try {
      const res: any = await addProduct(token, {
        ...data,
        isReferenced: true,
      });
      if (res?.error) {
        let msg = res.error || "";
        if (msg.includes("exist")) {
          msg = `Un produit avec la même référence produit existe déjà dans le catalogue, veuillez modifier la référence principale ou annuler la création du produit`;
        }
        setProductError(msg);
      } else {
        toggleOpenProductForm(false);
        setPrefilledReference("");
        await fetchInitialValues();
        setValue(`${fieldPath}.productReference`, data.reference);
        clearErrors(`${fieldPath}.productReference`);
        setProductError(null);
      }
    } catch (err) {
      console.error("error =>", err);
    }
  };

  const copyReference = () => {
    setValue(
      `${fieldPath}.productReference`,
      productReference?.originalProductReference
    );
  };

  const deleteNewReference = () => {
    setValue(`${fieldPath}.productReference`, "");
  };

  const infoMessage = useMemo(() => {
    if (hasAtLeastOneReceived(receivedQuantity)) return null;
    return "Un produit doit avoir été reçu pour être référencé.";
  }, [receivedQuantity]);

  const fetchUniqReference = useCallback(async () => {
    const isUniq = await debouncedIsReferenceUnique(
      getValues(`${fieldPath}.originalProductReference`),
      newProductReference,
      true
    );
    setStateReferenceUnique(isUniq);
  }, [
    fieldPath,
    getValues,
    debouncedIsReferenceUnique,
    newProductReference,
    setStateReferenceUnique,
  ]);

  useEffect(() => {
    if (
      !openDeleteModal &&
      confirmedDeletions &&
      deleteStatus === DeleteStatus.PENDING
    ) {
      setDeleteStatus(DeleteStatus.DELETED);
      setValue(`${fieldPath}.productReference`, "");
    }
  }, [openDeleteModal, deleteStatus, setValue, fieldPath, confirmedDeletions]);

  useEffect(() => {
    if (isTouched) {
      fetchUniqReference();
    }
  }, [fetchUniqReference, isTouched]);

  useEffect(() => {
    if (deleteStatus === DeleteStatus.DELETED) {
      setDeletedItems((prev: number[]) => [...prev, productReference.id]);
    }
  }, [
    deleteStatus,
    setDeletedItems,
    productReference,
    fieldPath,
    initialReference,
  ]);

  const productsOptions = useMemo(() => {
    return productFilter;
  }, [productFilter]);

  const autocompleteProps = {
    key: newProductReference,
    disabled:
      !hasReferencingCategory ||
      !hasAtLeastOneReceived(receivedQuantity) ||
      isReadOnly,
    options: productsOptions,
    isOptionEqualToValue: (option, value) => {
      if (!value || value === "") return false;
      if (!option) return false;
      return option.reference === value;
    },
    onInputChange: (e, data) => {
      setCurrentSearch(data);
    },
    filterOptions: (options, params) => {
      const filteredReference = options.filter(
        (opt: any) =>
          opt.reference &&
          opt.reference.toLowerCase().indexOf(params.inputValue.toLowerCase()) >
            -1
      );

      const filteredSecondReference = options.filter((opt: any) => {
        if (!opt.secondReference) {
          return false;
        }
        return (
          opt?.secondReference
            .toLowerCase()
            .indexOf(params.inputValue.toLowerCase()) > -1
        );
      });

      const filtered = filteredReference.concat(
        filteredSecondReference.filter(
          (itm) => filteredReference.indexOf(itm) < 0
        )
      );

      const { inputValue } = params;

      if (inputValue === "") {
        return [];
      }

      const isExisting = options.some(
        (option: any) =>
          inputValue.toUpperCase() === option.reference.toUpperCase()
      );

      if (inputValue !== "" && !isExisting) {
        filtered.push({
          inputValue,
          name: `Créer le produit "${inputValue}"`,
        });
      }

      return filtered;
    },
    onChange: (e, data) => {
      if (!data) {
        setValue(`${fieldPath}.productReference`, "");
        return;
      }

      if (data.inputValue) {
        setPrefilledReference(data.inputValue);
        toggleOpenProductForm(true);
        return;
      }

      setValue(`${fieldPath}.productReference`, data?.reference || "");
      clearErrors(`${fieldPath}.productReference`);
    },
    value:
      newProductReference && newProductReference !== ""
        ? getValueProduct(newProductReference)
        : null,
  };

  return {
    fieldPath,
    control,
    errors,
    listIndex,
    nestIndex,
    receivedQuantity,
    hasAtLeastOneReceived,
    hasReferencingCategory,
    isReadOnly,
    infoMessage,
    stateReferenceUnique,
    isTouched,
    openProductForm,
    toggleOpenProductForm,
    prefilledReference,
    setPrefilledReference,
    productError,
    setProductError,
    companyObject,
    onProductSubmit,
    copyReference,
    deleteNewReference,
    autocompleteProps,
  };
};
